



















































































import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import AlterationI18nMixin from '@/modules/budget/alteration/mixins/alteration-i18n-mixin';
import {
    IAlterationReason,
    IReasonGroup,
    IReasonTarget,
    REASON_MAX_LENGTH,
    saveReason,
    deleteReasons
} from './alteration-reason-api';

/**
 * Модалка добавления/редактирования примечания (причины отклонения) к специфике или проекту
 * заявки АБП (разрез "по АБП"). Разметка/классы - по образцу
 * front/src/modules/budget-request/components/c-set-bf-category-name.vue.
 * Режим просмотра (view) - Справки (расходы), EAK-808: поля ru/kk только для чтения, без сохранения/удаления.
 */
@Component({ name: 'c-reason-modal' })
export default class CReasonModal extends mixins(AlterationI18nMixin) {
    @Prop({ required: true })
    private requestId!: number | string;

    @Prop({
        required: false,
        default: null
    })
    private region!: string | null;

    @Prop({
        required: false,
        default: null
    })
    private year!: number | string | null;

    @Prop({
        required: false,
        default: null
    })
    private abp!: number | null;

    private readonly REASON_MAX_LENGTH = REASON_MAX_LENGTH;

    private isModalShown = false;
    private isEditMode = false;
    private isViewMode = false;
    private isLoading = false;

    private target: IReasonTarget | null = null;

    private reasonRu = '';
    private reasonKz = '';
    private initialReasonRu = '';
    private initialReasonKz = '';

    private get modalTitle() {
        if (this.isViewMode) { return this.setText('reason.view_title'); }
        return this.isEditMode ? this.setText('reason.edit_title') : this.setText('reason.add_title');
    }

    private get okBtnName() {
        return this.isEditMode ? this.setText('reason.save') : this.setText('reason.add');
    }

    private get isChanged() {
        return this.reasonRu.trim() !== this.initialReasonRu.trim()
            || this.reasonKz.trim() !== this.initialReasonKz.trim();
    }

    private get isSaveDisabled() {
        if (this.isLoading) { return true; }
        if (!this.isEditMode) {
            return !this.reasonRu.trim() && !this.reasonKz.trim();
        }
        return !this.isChanged;
    }

    // Открытие модалки на добавление (target.group === null) либо редактирование (target.group заполнен)
    public open(target: IReasonTarget) {
        this.target = target;
        this.isViewMode = false;
        this.isEditMode = !!(target.group && target.group.reason);
        const reason = target.group ? target.group.reason : null;
        this.reasonRu = (reason && reason.reasonRu) || '';
        this.reasonKz = (reason && reason.reasonKz) || '';
        this.initialReasonRu = this.reasonRu;
        this.initialReasonKz = this.reasonKz;
        this.isModalShown = true;
    }

    // Открытие в режиме просмотра (Справки (расходы), EAK-808). target не задаётся - onSaveClick/deleteReason
    // без него ничего не делают; в group.reason тексты причин всех заявок АБП, склеенные через перевод строки.
    // group === null - примечания нет: поля пустые с подсказкой «Примечание не указано»
    public view(group: IReasonGroup | null) {
        this.target = null;
        this.isViewMode = true;
        this.isEditMode = false;
        this.reasonRu = (group && group.reason.reasonRu) || '';
        this.reasonKz = (group && group.reason.reasonKz) || '';
        this.initialReasonRu = this.reasonRu;
        this.initialReasonKz = this.reasonKz;
        this.isModalShown = true;
    }

    private hideModal() {
        if (this.isLoading) { return; }
        this.isModalShown = false;
    }

    // Пока идёт запрос, не даём закрыть модалку крестиком/ESC/кликом по фону - закрытие только по результату
    private onHide(bvEvent: any) {
        if (this.isLoading && bvEvent.trigger !== null) { bvEvent.preventDefault(); }
    }

    private resetForm() {
        this.target = null;
        this.isEditMode = false;
        this.isViewMode = false;
        this.reasonRu = '';
        this.reasonKz = '';
        this.initialReasonRu = '';
        this.initialReasonKz = '';
        this.isLoading = false;
    }

    private async onSaveClick() {
        if (!this.target) { return; }
        const reasonRu = this.reasonRu.trim();
        const reasonKz = this.reasonKz.trim();

        // оба поля очищены в режиме редактирования - трактуем как удаление примечания
        if (this.isEditMode && !reasonRu && !reasonKz) {
            await this.deleteReason();
            return;
        }

        this.isLoading = true;
        try {
            const dto: Partial<IAlterationReason> = (this.isEditMode && this.target.group)
                ? {
                    ...this.target.group.reason,
                    reasonRu: reasonRu || null,
                    reasonKz: reasonKz || null
                }
                : {
                    id: null,
                    budgetExecutionAlterationAbpRequestId: this.requestId,
                    region: this.region,
                    year: this.year,
                    abp: this.abp,
                    gr: this.target.gr,
                    prg: this.target.prg,
                    ppr: this.target.ppr,
                    spf: this.target.spf,
                    expenseCode: this.target.expenseCode,
                    planType: this.target.planType,
                    reasonRu: reasonRu || null,
                    reasonKz: reasonKz || null
                };
            await saveReason(dto);
            this.makeToast('success', this.setText('message'), this.setText('reason.saved'));
            this.isModalShown = false;
            this.$emit('changed');
        } catch (error) {
            this.makeToast('danger', this.setText('reason.save_error'), error.toString());
        } finally {
            this.isLoading = false;
        }
    }

    private onDeleteClick() {
        this.$bvModal.msgBoxConfirm(
            this.setText('reason.delete_confirm'),
            {
                title: this.setText('reason.confirmation'),
                size: 'lg',
                buttonSize: 'sm',
                okVariant: 'success',
                okTitle: this.setText('reason.yes'),
                cancelTitle: this.setText('reason.cancel'),
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
            .then(value => {
                if (value) { this.deleteReason(); }
            })
            .catch(error => {
                this.makeToast('danger', this.setText('reason.delete_error'), error.toString());
            });
    }

    private async deleteReason() {
        if (!this.target || !this.target.group) { return; }
        this.isLoading = true;
        try {
            await deleteReasons(this.target.group.ids);
            this.makeToast('success', this.setText('message'), this.setText('reason.deleted'));
            this.isModalShown = false;
            this.$emit('changed');
        } catch (error) {
            this.makeToast('danger', this.setText('reason.delete_error'), error.toString());
        } finally {
            this.isLoading = false;
        }
    }
}
